(function () {
  const DEFAULT_DURATION = 0.9;
  const DEFAULT_WHEEL_MULTIPLIER = 0.75;
  const LINE_HEIGHT = 16;
  const SCROLLABLE_OVERFLOW_PATTERN = /(auto|scroll|overlay)/;

  let isEnabled = false;
  let isPaused = false;
  let scrollTween = null;
  let targetY = getScrollY();
  let currentY = targetY;
  let isTweening = false;

  function getScrollY() {
    return window.scrollY || window.pageYOffset || 0;
  }

  function getMaxScrollY() {
    return Math.max(
      0,
      Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight
    );
  }

  function clampScrollY(value) {
    return Math.min(Math.max(value, 0), getMaxScrollY());
  }

  function getSettingsSource() {
    const stack = document.querySelector('large-product-cards-stack');

    return stack ? stack.closest('.hp-large-product-cards') : document.documentElement;
  }

  function getSmoothScrollSettings() {
    const styles = window.getComputedStyle(getSettingsSource());
    const durationMs = Number.parseFloat(
      styles.getPropertyValue('--large-product-cards-page-scroll-duration')
    );
    const wheelMultiplierPercent = Number.parseFloat(
      styles.getPropertyValue('--large-product-cards-page-scroll-wheel-multiplier')
    );

    return {
      duration:
        Number.isFinite(durationMs) && durationMs > 0
          ? durationMs / 1000
          : DEFAULT_DURATION,
      wheelMultiplier:
        Number.isFinite(wheelMultiplierPercent) && wheelMultiplierPercent > 0
          ? wheelMultiplierPercent / 100
          : DEFAULT_WHEEL_MULTIPLIER,
    };
  }

  function getElementFromEvent(event) {
    if (typeof event.composedPath === 'function') {
      const path = event.composedPath();
      const fromPath = path.find((node) => node instanceof Element);
      if (fromPath) return fromPath;
    }

    if (event.target instanceof Element) return event.target;

    return event.target && event.target.parentElement;
  }

  function closestPiercing(element, selector) {
    let current = element;

    while (current && current !== document.body && current !== document.documentElement) {
      if (current.matches?.(selector)) return current;
      current = getParentElement(current);
    }

    return null;
  }

  function isEditableElement(element) {
    return Boolean(element && element.closest('input, textarea, select, [contenteditable="true"]'));
  }

  function getParentElement(element) {
    if (!element) return null;
    if (element.parentElement) return element.parentElement;

    const root = element.getRootNode && element.getRootNode();
    return root && root.host ? root.host : null;
  }

  function canScrollWithin(element, deltaY) {
    let currentElement = element;

    while (
      currentElement &&
      currentElement !== document.body &&
      currentElement !== document.documentElement
    ) {
      const style = window.getComputedStyle(currentElement);
      const isScrollable = SCROLLABLE_OVERFLOW_PATTERN.test(style.overflowY);

      if (isScrollable && currentElement.scrollHeight > currentElement.clientHeight) {
        const scrollTop = currentElement.scrollTop;
        const canScrollUp = scrollTop > 0;
        const canScrollDown =
          scrollTop + currentElement.clientHeight < currentElement.scrollHeight - 1;

        if ((deltaY < 0 && canScrollUp) || (deltaY > 0 && canScrollDown)) {
          return true;
        }
      }

      currentElement = getParentElement(currentElement);
    }

    return false;
  }

  function normalizeWheelDelta(event) {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * LINE_HEIGHT;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * window.innerHeight;

    return event.deltaY;
  }

  function animateScrollTo(nextY) {
    const settings = getSmoothScrollSettings();

    targetY = clampScrollY(nextY);
    scrollTween && scrollTween.kill();

    scrollTween = window.gsap.to(
      { y: currentY },
      {
        y: targetY,
        duration: settings.duration,
        ease: 'power3.out',
        overwrite: true,
        onStart: function () {
          isTweening = true;
        },
        onUpdate: function () {
          currentY = this.targets()[0].y;
          window.scrollTo(0, currentY);
        },
        onComplete: function () {
          isTweening = false;
          currentY = getScrollY();
          targetY = currentY;
          scrollTween = null;
        },
      }
    );
  }

  function isDocumentScrollLocked() {
    return (
      document.documentElement.classList.contains('claim-reward-modal-open') ||
      document.documentElement.classList.contains('cart-drawer-open') ||
      document.documentElement.classList.contains('bf-bv-modal-open') ||
      document.documentElement.style.overflow === 'hidden'
    );
  }

  function stopScrollTween() {
    scrollTween && scrollTween.kill();
    scrollTween = null;
    isTweening = false;
    currentY = getScrollY();
    targetY = currentY;
  }

  function shouldIgnoreWheel(event) {
    const element = getElementFromEvent(event);

    return (
      event.defaultPrevented ||
      !event.cancelable ||
      event.ctrlKey ||
      !element ||
      isEditableElement(element) ||
      Boolean(
        closestPiercing(
          element,
          '[data-page-smooth-scroll-ignore], [data-large-product-cards-smooth-scroll-ignore], .bv-ips-modal-window, [class*="ips_"], #review-submission'
        )
      ) ||
      canScrollWithin(element, event.deltaY)
    );
  }

  function pause() {
    if (isPaused) return;
    isPaused = true;
    stopScrollTween();
  }

  function resume() {
    if (!isPaused) return;
    isPaused = false;
    syncScrollPosition();
  }

  function onWheel(event) {
    if (isPaused) return;

    if (isDocumentScrollLocked()) {
      stopScrollTween();
      const element = getElementFromEvent(event);
      if (element && canScrollWithin(element, event.deltaY)) return;
      if (event.cancelable) event.preventDefault();
      return;
    }

    if (shouldIgnoreWheel(event)) return;

    const deltaY = normalizeWheelDelta(event);

    if (!deltaY || Math.abs(event.deltaX) > Math.abs(deltaY)) return;

    const settings = getSmoothScrollSettings();

    event.preventDefault();
    animateScrollTo(targetY + deltaY * settings.wheelMultiplier);
  }

  function getKeyboardScrollTarget(event) {
    const viewportStep = window.innerHeight * 0.85;

    switch (event.key) {
      case 'ArrowDown':
        return targetY + 80;
      case 'ArrowUp':
        return targetY - 80;
      case 'PageDown':
        return targetY + viewportStep;
      case 'PageUp':
        return targetY - viewportStep;
      case ' ':
      case 'Spacebar':
        return targetY + (event.shiftKey ? -viewportStep : viewportStep);
      case 'Home':
        return 0;
      case 'End':
        return getMaxScrollY();
      default:
        return null;
    }
  }

  function onKeyDown(event) {
    if (isPaused) return;

    if (isDocumentScrollLocked()) {
      stopScrollTween();
      return;
    }

    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      isEditableElement(document.activeElement)
    ) {
      return;
    }

    const nextY = getKeyboardScrollTarget(event);

    if (nextY === null) return;

    event.preventDefault();
    animateScrollTo(nextY);
  }

  function syncScrollPosition() {
    if (isTweening) return;

    currentY = getScrollY();
    targetY = currentY;
  }

  function enable() {
    if (isEnabled) return;

    isEnabled = true;
    syncScrollPosition();
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', syncScrollPosition, { passive: true });
    window.addEventListener('resize', syncScrollPosition);
  }

  function disable() {
    if (!isEnabled) return;

    isEnabled = false;
    scrollTween && scrollTween.kill();
    scrollTween = null;
    isTweening = false;
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('scroll', syncScrollPosition);
    window.removeEventListener('resize', syncScrollPosition);
    syncScrollPosition();
  }

  function setup() {
    if (!window.gsap || !window.matchMedia) return;

    const mediaQuery = window.matchMedia(
      '(prefers-reduced-motion: no-preference) and (pointer: fine)'
    );
    const syncEnabledState = function () {
      if (mediaQuery.matches) {
        enable();
      } else {
        disable();
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncEnabledState);
    } else {
      mediaQuery.addListener(syncEnabledState);
    }

    syncEnabledState();

    const lockObserver = new MutationObserver(function () {
      if (isDocumentScrollLocked()) stopScrollTween();
    });

    lockObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  window.backflipPageSmoothScroll = {
    pause,
    resume,
    isPaused: function () {
      return isPaused;
    },
  };

  setup();
})();
